import React from 'react';
import {
  PACKAGE_VISIBILITY_LABEL,
  PackageVisibility,
  VehicleCondition,
} from '@/types/package';
import { conditionLabels } from '@components/HandoverDetail/handoverDetailDisplay';

export interface StatusProps {
  value: string;
  type?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

interface StatusConfig {
  label: string;
  bgColor: string;
  textColor: string;
  /** Optional leading status dot, as a Tailwind bg-* class. */
  dotColor?: string;
  /** Optional leading icon class (Remix Icon), used instead of a dot. */
  icon?: string;
  /** Optional border, e.g. 'border border-[#EBEBEB]'. */
  borderColor?: string;
  /** Corner radius override; defaults to a full pill. */
  rounded?: string;
  /** Padding override for designs that need exact metrics. */
  padding?: string;
}

// Jeda Tagihan: billing stopped on purpose (unit in the workshop), so amber
// rather than red. One look wherever "Dijeda" shows — the Eskalasi column, the
// ledger separator and the timeline (RESPONSIVE_WEB_PLAN interim tokens).
const BILLING_PAUSED: StatusConfig = {
  label: 'Dijeda',
  bgColor: 'bg-amber-100',
  textColor: 'text-amber-700',
};

// Status configurations for different types
const statusConfigs: Record<string, Record<string, StatusConfig>> = {
  contract: {
    RTO: {
      label: 'Rent to Own',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    RENT: {
      label: 'Rent',
      bgColor: 'bg-orange-100',
      textColor: 'text-[#FA7319]',
    },
  },

  // Dash Rent package contract type. Kept separate from `contract` above,
  // which renders RENT in orange for the Models/Vehicles tables.
  rentType: {
    RTO: {
      label: 'Rent To Own',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    RENT: {
      label: 'Rent',
      bgColor: 'bg-[#EBF1FF]',
      textColor: 'text-[#335CFF]',
    },
  },

  // Package catalog lifecycle. ARCHIVED is distinct from INACTIVE: inactive
  // is withheld from matching but still editable, archived is retired.
  packageStatus: {
    ACTIVE: {
      label: 'Active',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
    },
    INACTIVE: {
      label: 'Inactive',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    ARCHIVED: {
      label: 'Archived',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
  },

  packageVisibility: {
    [PackageVisibility.PUBLIC]: {
      label: PACKAGE_VISIBILITY_LABEL[PackageVisibility.PUBLIC],
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    [PackageVisibility.PRIVATE]: {
      label: PACKAGE_VISIBILITY_LABEL[PackageVisibility.PRIVATE],
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
  },

  // Keyed by the label `holderStatusLabel` derives, colours as basecamp.
  campaignStatus: {
    Draf: { label: 'Draf', bgColor: 'bg-gray-100', textColor: 'text-gray-600' },
    Terjadwal: {
      label: 'Terjadwal',
      bgColor: 'bg-[#EBF1FF]',
      textColor: 'text-[#335CFF]',
    },
    Aktif: {
      label: 'Aktif',
      bgColor: 'bg-[#DCFCE7]',
      textColor: 'text-[#15803D]',
    },
    Selesai: {
      label: 'Selesai',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    Dijeda: {
      label: 'Dijeda',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    Diarsipkan: {
      label: 'Diarsipkan',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
    },
  },

  // Payment History rows on the rental detail. Icons rather than dots, per
  // the design.
  rentalCollection: {
    PAID: {
      label: 'Success',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
      icon: 'ri-checkbox-circle-fill',
    },
    PENDING: {
      label: 'Pending',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
      icon: 'ri-alert-fill',
    },
    FAILED: {
      label: 'Failed',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
      icon: 'ri-error-warning-fill',
    },
    EXPIRED: {
      label: 'Expired',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
      icon: 'ri-time-fill',
    },
    // A method switch or a called-off booking, not a failure — so it reads
    // neutral like Expired, told apart by icon and label rather than colour.
    CANCELLED: {
      label: 'Cancelled',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
      icon: 'ri-close-circle-fill',
    },
  },

  // Daily charge ledger rows on the rental detail. Deliberately not reusing
  // `rentalCollection`: a charge is a day that was billed, not a payment
  // attempt, so it has WAIVED (a free day) and no FAILED/EXPIRED, and the
  // labels are the ledger's Indonesian wording rather than "Success".
  //
  // A free day reads green like a paid one because the day is settled either
  // way — nothing is owed on it.
  rentalCharge: {
    PAID: {
      label: 'Lunas',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
      icon: 'ri-checkbox-circle-fill',
    },
    PENDING: {
      label: 'Belum Bayar',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
      icon: 'ri-alert-fill',
    },
    WAIVED: {
      label: 'Gratis',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
      icon: 'ri-gift-fill',
    },
    // Not a failure: the day was struck from the ledger (an early return, or
    // a cancelled booking), so nothing is owed and nothing was paid.
    CANCELLED: {
      label: 'Dibatalkan',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
      icon: 'ri-close-circle-fill',
    },
  },

  // Rental CONTRACT status (Rent Activity list + detail header). Where the unit
  // physically is has its own variant below — `handoverStatus` — because the
  // rental reads ACTIVE whether the bike is at the hub or on the road.
  //
  // TERMINATED is a display-only value: ENDED carrying an *abnormal*
  // termination_reason renders as Terminated (TRD B0).
  //
  // Colour follows how committed the contract is: blue while nothing is
  // committed, yellow while the driver still owes the DP, purple once it is
  // live, neutral at the end, red for a bad ending.
  rentalStatus: {
    PRE_ORDER: {
      label: 'Pre Order',
      bgColor: 'bg-[#EBF1FF]',
      textColor: 'text-[#335CFF]',
    },
    // Unit reserved, DP unpaid — the ball is in the driver's court.
    REQUEST_BOOKING: {
      label: 'Request Booking',
      bgColor: 'bg-[#FFFAEB]',
      textColor: 'text-[#F6B51E]',
    },
    ACTIVE: {
      label: 'Active',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    ENDED: {
      label: 'Selesai',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    // A filled RTO tenor: the driver owns the bike. The best outcome the
    // product has, so it reads green rather than the neutral grey of a return.
    COMPLETED: {
      label: 'Lunas (RTO)',
      bgColor: 'bg-[#E6F7EC]',
      textColor: 'text-[#1A8245]',
    },
    TERMINATED: {
      label: 'Terminated',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
    CANCELLED: {
      label: 'Cancelled',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
  },

  // Where the UNIT is. Read alongside rentalStatus, never instead of it.
  //
  // Colour tracks possession rather than commitment: orange while ops still
  // owes an action, purple while the unit is out, neutral once it is back, red
  // for a slot that was burned.
  handoverStatus: {
    // DP paid, pickup outstanding — the ball is in ops' court.
    WAITING_HANDOVER: {
      label: 'Menunggu Serah Terima',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    ACTIVE: {
      label: 'Di Mitra',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    // Deliberately identical to ACTIVE: a scheduled return still leaves the
    // unit with the driver, still bills and still escalates, so it must not
    // look like the rental is over. The label alone carries the difference.
    WAITING_RETURN: {
      label: 'Menunggu Pengembalian',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    RETURNED: {
      label: 'Dikembalikan',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    // Left the fleet through a sale, not a return — the two must not look alike.
    RELEASED: {
      label: 'Lepas (RTO)',
      bgColor: 'bg-[#E6F7EC]',
      textColor: 'text-[#1A8245]',
    },
    NO_SHOW: {
      label: 'No Show',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
    CANCELLED: {
      label: 'Dibatalkan',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
  },

  // Driver outstanding lifecycle (damage settlement debt, TRD Part 3).
  // OPEN/PARTIALLY_SETTLED still owe money (red/amber — the driver hasn't
  // cleared it); SETTLED is the good ending (green); RELEASED is neutral —
  // written off, no longer anyone's action item.
  driverOutstandingStatus: {
    OPEN: {
      label: 'Open',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
    PARTIALLY_SETTLED: {
      label: 'Partially Settled',
      bgColor: 'bg-[#FFFAEB]',
      textColor: 'text-[#F6B51E]',
    },
    SETTLED: {
      label: 'Settled',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
    },
    RELEASED: {
      label: 'Released',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
  },

  // Late-payment escalation ladder (TRD L3). Severity is the ladder itself,
  // not the day count — the rung is what decides the consequence, so it is
  // what the colour tracks.
  //
  // NORMAL is intentionally absent: a rental that owes nothing gets no pill
  // at all rather than a reassuring green one. Callers guard on it (see
  // `showsEscalationPill`) instead of rendering the grey fallback.
  //
  // Labels and hues are the ones in ESCALATION_STATUS_COLOR /
  // ESCALATION_STATUS_LABEL (src/types/rental.ts) — kept in step by hand
  // because Tailwind needs the literals at build time. REPO_REQUESTED used to
  // share SHUTDOWN's red, which hid the one rung that sends someone out to
  // the unit behind the one that only asks ops to switch it off.
  escalationStatus: {
    WARNED: {
      label: 'Diperingatkan',
      bgColor: 'bg-[#FFFAEB]',
      textColor: 'text-[#F6B51E]',
    },
    SHUTDOWN_PENDING: {
      label: 'Wajib dimatikan',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    SHUTDOWN: {
      label: 'Sudah dimatikan',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
    REPO_REQUESTED: {
      label: 'Perlu ditarik',
      bgColor: 'bg-[#FDE7EC]',
      textColor: 'text-[#7A0C2E]',
    },
    // Paused by ops on purpose, so it reads neutral: the debt is still there,
    // but no consequence is coming while the hold stands.
    HOLD: {
      label: 'Eskalasi dijeda',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    // Not a rung: shown instead of the (frozen) rung while billing is paused.
    billingPaused: BILLING_PAUSED,
  },

  // Chips on the rental detail timeline.
  timelineEvent: {
    PAID: {
      label: 'Paid',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
    },
    OVERDUE: {
      label: 'Overdue',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    PENDING: {
      label: 'Pending',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    WAIVED: {
      label: 'Hari Gratis',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
    },
    ROUTINE_CHECK: {
      label: 'Routine Check',
      bgColor: 'bg-[#EBF1FF]',
      textColor: 'text-[#335CFF]',
    },
    START: {
      label: 'Start',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    BOOKING: {
      label: 'Booking',
      bgColor: 'bg-[#EBF1FF]',
      textColor: 'text-[#335CFF]',
    },
    END: {
      label: 'End',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    SHUTDOWN: {
      label: 'Shutdown',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
    REFUNDED: {
      label: 'Refunded',
      bgColor: 'bg-[#EBF1FF]',
      textColor: 'text-[#335CFF]',
    },
    REASSIGN: {
      label: 'Ganti Unit',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    billingPaused: BILLING_PAUSED,
    billingResumed: {
      label: 'Dilanjutkan',
      bgColor: 'bg-primary-50',
      textColor: 'text-primary-600',
    },
  },

  feedback: {
    complete: {
      label: 'Complete',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
    },
    completed: {
      label: 'Completed',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
    },
    active: {
      label: 'Active',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
    },
    approved: {
      label: 'Approved',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
    },
    delivered: {
      label: 'Delivered',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
    },
    online: {
      label: 'Online',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
    },

    // Inactive/Not Active states
    inactive: {
      label: 'Not Active',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
    },
    'not active': {
      label: 'Not Active',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
    },

    // Warning states
    'in delivery': {
      label: 'In Delivery',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
    },
    'in progress': {
      label: 'In Progress',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
    },
    'pending pickup': {
      label: 'Pending Pickup',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
    },
    'picking up': {
      label: 'Picking Up',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
    },
    processing: {
      label: 'Processing',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
    },

    // Error states
    failed: {
      label: 'Failed',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
    },
    error: {
      label: 'Error',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
    },
    rejected: {
      label: 'Rejected',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
    },
    offline: {
      label: 'Offline',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
    },
    unavailable: {
      label: 'Unavailable',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
    },

    // Info states
    allocating: {
      label: 'Allocating',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
    },
    queueing: {
      label: 'Queueing',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
    },
    scheduled: {
      label: 'Scheduled',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
    },
    reviewing: {
      label: 'Reviewing',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
    },

    // Neutral states
    canceled: {
      label: 'Canceled',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
    },
    cancelled: {
      label: 'Cancelled',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
    },
    draft: {
      label: 'Draft',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
    },
    idle: {
      label: 'Idle',
      bgColor: 'bg-[#FFFAEB]',
      textColor: 'text-[#F6B51E]',
    },

    // Maintenance states
    maintenance: {
      label: 'Maintenance',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
    'in repair': {
      label: 'In Repair',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },

    // Broken states
    broken: {
      label: 'Broken',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },

    // Pending states
    pending: {
      label: 'Pending',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
    },
    waiting: {
      label: 'Waiting',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
    },
    hold: {
      label: 'Hold',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
    },
  },

  // Whether a unit counts as a package's stock, and the swap's override flag.
  packageVehicle: {
    AVAILABLE: {
      label: 'Tersedia',
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
    },
    UNAVAILABLE: {
      label: 'Tidak tersedia',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-600',
    },
    REQUIRES_OVERRIDE: {
      label: 'Perlu override',
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
  },

  // Unit quality grade (VehicleCondition), e.g. on the reassign-unit picker.
  vehicleCondition: {
    [VehicleCondition.GOOD]: {
      label: conditionLabels.GOOD,
      bgColor: 'bg-[#E0FAEC]',
      textColor: 'text-[#1FC16B]',
    },
    [VehicleCondition.MINOR_DAMAGE]: {
      label: conditionLabels.MINOR_DAMAGE,
      bgColor: 'bg-[#FFF3EB]',
      textColor: 'text-[#FA7319]',
    },
    [VehicleCondition.MAJOR_DAMAGE]: {
      label: conditionLabels.MAJOR_DAMAGE,
      bgColor: 'bg-red-50',
      textColor: 'text-red-600',
    },
  },

  handover: {
    returned: {
      label: 'Returned',
      bgColor: 'bg-[#EFEAF7]',
      textColor: 'text-[#5E2AAC]',
    },
  },

  // STNK document expiry status (see src/pages/stnk)
  stnk: {
    kadaluwarsa: {
      label: 'Kadaluwarsa',
      bgColor: 'bg-[#FFEBEC]',
      textColor: 'text-[#FB3748]',
    },
    sangat_mendesak: {
      label: 'Sangat Mendesak',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-700',
    },
    akan_habis: {
      label: 'Akan Habis',
      bgColor: 'bg-[#FFFAEB]',
      textColor: 'text-[#F6B51E]',
    },
    aman: {
      label: 'Aman',
      bgColor: 'bg-green-100',
      textColor: 'text-green-700',
    },
  },
};

// One look per TYPE, for a type whose values are not a closed set.
//
// `packageScheme` is the case that forced this: package names became free text,
// so styling them per name means the four that were once hardcoded (Irit, Hemat,
// Regular, Platinum) each kept their own dot colour while every package created
// since — Premium, and anything ops name next — dropped through to the generic
// grey pill. One chip for every package, whatever it is called.
const typeFallbacks: Record<string, Omit<StatusConfig, 'label'>> = {
  packageScheme: {
    bgColor: 'bg-white',
    textColor: 'text-[#5C5C5C]',
    dotColor: 'bg-[#8A8A8A]',
    borderColor: 'border border-[#EBEBEB]',
    rounded: 'rounded-md',
    padding: 'px-2.5 py-1',
  },
};

export const Status: React.FC<StatusProps> = ({
  value,
  type = 'general',
  size = 'medium',
  className = '',
}) => {
  // Get the configuration for this status
  const typeConfigs = statusConfigs[type] || {};
  const config = typeConfigs[value] || typeConfigs[value.toLowerCase()];

  // Fallback for unknown statuses: the type's own look when it has one (the
  // value is data, not an enum member), else the generic grey pill.
  const fallbackConfig: StatusConfig = {
    label: value,
    ...(typeFallbacks[type] ?? {
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
    }),
  };

  const finalConfig = config || fallbackConfig;

  // Size variants — padding and text size are kept apart so a config can
  // override the padding alone without losing the size's type scale.
  const sizePadding = {
    small: 'px-2 py-0.5',
    medium: 'px-2.5 py-0.5',
    large: 'px-3 py-1',
  };

  const sizeText = {
    small: 'text-xs',
    medium: 'text-xs',
    large: 'text-sm',
  };

  // Base classes
  const baseClasses = 'inline-flex items-center font-medium';

  // Build final classes
  const finalClasses = `${baseClasses} ${
    finalConfig.rounded || 'rounded-full'
  } ${finalConfig.padding || sizePadding[size]} ${sizeText[size]} ${
    finalConfig.bgColor
  } ${finalConfig.textColor} ${finalConfig.borderColor || ''} ${className}`
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <span className={finalClasses}>
      {finalConfig.icon && (
        <i className={`${finalConfig.icon} mr-1 text-[13px] leading-none`}></i>
      )}
      {!finalConfig.icon && finalConfig.dotColor && (
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 shrink-0 ${finalConfig.dotColor}`}
        />
      )}
      {finalConfig.label}
    </span>
  );
};

export default Status;
